// Solver schema helpers derived from the canonical mapping schema:
// solver types, default per-chain selection, and solver property normalization.
import { APPENDAGE_HINTS, FINGER_ROLE_NAMES, type AppendageType } from './namingCatalog';

export const DEFAULT_SOLVER_ITERATIONS = 4;
export const DEFAULT_SOLVER_REVERSE_PV = false;
export const DEFAULT_SOLVER_MAX_STRETCH = 1.0;

export const DEFAULT_SPRING_STIFFNESS = 0.5;
export const DEFAULT_SPRING_FALLOFF = 0.5;
export const DEFAULT_SPRING_FALLOFF_CURVE = 'linear';
export const DEFAULT_SPRING_GAIN = 1.0;
export const SPRING_GAIN_UI_MIN = 0;
export const SPRING_GAIN_UI_MAX = 2;
export const SPRING_FALLOFF_CURVES = [
  'linear',
  'quadratic',
  'sqrt',
  'exponential',
] as const;

const APPENDAGE_CHAIN_PREFIXES = Object.keys(APPENDAGE_HINTS) as AppendageType[];
const ITERATIVE_CHAIN_PATTERN = /_\d+$/;

export type SpringFalloffCurve = (typeof SPRING_FALLOFF_CURVES)[number];

function roundSolverValue(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clampSpringUnitValue(value: number): number {
  return clamp(value, 0, 1);
}

export function clampSpringGainForControl(value: number): number {
  return clamp(value, SPRING_GAIN_UI_MIN, SPRING_GAIN_UI_MAX);
}

function isKnownSpringFalloffCurve(value: unknown): value is SpringFalloffCurve {
  if (typeof value !== 'string') return false;
  return (SPRING_FALLOFF_CURVES as readonly string[]).includes(value);
}

export class SolverProperties {
  iterations: number;
  reversePv: boolean;
  maxStretch: number;

  constructor(
    iterations = DEFAULT_SOLVER_ITERATIONS,
    reversePv = DEFAULT_SOLVER_REVERSE_PV,
    maxStretch = DEFAULT_SOLVER_MAX_STRETCH,
  ) {
    this.iterations = iterations;
    this.reversePv = reversePv;
    this.maxStretch = maxStretch;
  }

  get isDefault(): boolean {
    return (
      this.iterations === DEFAULT_SOLVER_ITERATIONS &&
      this.reversePv === DEFAULT_SOLVER_REVERSE_PV &&
      this.maxStretch === DEFAULT_SOLVER_MAX_STRETCH
    );
  }

  toDict(): Record<string, unknown> {
    return {
      iterations: this.iterations,
      revPole: this.reversePv,
      maxStretch: roundSolverValue(this.maxStretch),
    };
  }

  static fromDict(data: Record<string, unknown>): SolverProperties {
    const maxStretch =
      typeof data.maxStretch === 'number' && Number.isFinite(data.maxStretch)
        ? data.maxStretch
        : DEFAULT_SOLVER_MAX_STRETCH;
    return new SolverProperties(
      typeof data.iterations === 'number' && Number.isFinite(data.iterations)
        ? data.iterations
        : DEFAULT_SOLVER_ITERATIONS,
      Boolean(data.revPole ?? DEFAULT_SOLVER_REVERSE_PV),
      maxStretch,
    );
  }
}

export class SpringSolverProperties {
  stiffness: number;
  falloff: number;
  falloffCurve: SpringFalloffCurve;
  gain: number;

  constructor(
    stiffness = DEFAULT_SPRING_STIFFNESS,
    falloff = DEFAULT_SPRING_FALLOFF,
    falloffCurve: SpringFalloffCurve = DEFAULT_SPRING_FALLOFF_CURVE,
    gain = DEFAULT_SPRING_GAIN,
  ) {
    this.stiffness = clampSpringUnitValue(stiffness);
    this.falloff = clampSpringUnitValue(falloff);
    this.falloffCurve = falloffCurve;
    this.gain = gain;
  }

  get isDefault(): boolean {
    return (
      this.stiffness === DEFAULT_SPRING_STIFFNESS &&
      this.falloff === DEFAULT_SPRING_FALLOFF &&
      this.falloffCurve === DEFAULT_SPRING_FALLOFF_CURVE &&
      this.gain === DEFAULT_SPRING_GAIN
    );
  }

  toDict(): Record<string, unknown> {
    return {
      stiffness: roundSolverValue(this.stiffness),
      falloff: roundSolverValue(this.falloff),
      falloffCurve: this.falloffCurve,
      gain: roundSolverValue(this.gain),
    };
  }

  static fromDict(data: Record<string, unknown>): SpringSolverProperties {
    const stiffness =
      typeof data.stiffness === 'number' && Number.isFinite(data.stiffness)
        ? data.stiffness
        : DEFAULT_SPRING_STIFFNESS;
    const falloff =
      typeof data.falloff === 'number' && Number.isFinite(data.falloff)
        ? data.falloff
        : DEFAULT_SPRING_FALLOFF;
    const falloffCurve = isKnownSpringFalloffCurve(data.falloffCurve)
      ? data.falloffCurve
      : DEFAULT_SPRING_FALLOFF_CURVE;
    const gain =
      typeof data.gain === 'number' && Number.isFinite(data.gain)
        ? data.gain
        : DEFAULT_SPRING_GAIN;
    return new SpringSolverProperties(stiffness, falloff, falloffCurve, gain);
  }
}

export type ChainSolverProperties = SolverProperties | SpringSolverProperties;

export enum SolverType {
  Look = 'LookSolver',
  Tri = 'TriSolver',
  Z = 'ZSolver',
  AsincArc = 'AsincSolver',
  SwingTwistChain = 'SwingTwistChainSolver',
  SwingTwistMove = 'SwingTwistMoveSolver',
  SpringChain = 'SpringChainSolver',
}

/** Short UI labels for solver type selects (matches backend string values). */
export const SOLVER_LABELS: Record<SolverType, string> = {
  [SolverType.Look]: 'LookSolver',
  [SolverType.Tri]: 'TriSolver',
  [SolverType.Z]: 'ZSolver',
  [SolverType.AsincArc]: 'ASincArcSolver',
  [SolverType.SwingTwistChain]: 'SwingTwistChainSolver',
  [SolverType.SwingTwistMove]: 'SwingTwistMoveSolver',
  [SolverType.SpringChain]: 'SpringChainSolver',
};

/** Human-readable explanations for each solver type (dropdown + docs). */
export const SOLVER_DESCRIPTIONS: Record<SolverType, string> = {
  [SolverType.Look]:
    'Single-joint aim solver. (Default for head, clavicles, feet and fingers)',
  [SolverType.Tri]: '2-bone IK using triangle. (Default for limbs)',
  [SolverType.Z]: '3-bone IK Z-shape chain (Recommended for hock-style leg chains)',
  [SolverType.AsincArc]: 'Arc-based solver for multi-link chains',
  [SolverType.SwingTwistChain]:
    'Distributes swing/twist across a chain by interpolating between start and end targets. (Default for spine)',
  [SolverType.SwingTwistMove]:
    'Single-joint solver applying positional offset plus swing/twist rotation. (Default for pelvis and root)',
  [SolverType.SpringChain]:
    'Rotation follow-through chain solver with trailing motion. (Default for tails, tentacles, and antennae)',
};

export const SOLVER_TYPES = Object.values(SolverType) as SolverType[];

export const DEFAULT_SOLVERS: Record<string, SolverType> = {
  Root: SolverType.SwingTwistMove,
  Pelvis: SolverType.SwingTwistMove,
  Spine: SolverType.SwingTwistChain,
  Neck: SolverType.SwingTwistChain,
  Head: SolverType.Look,
  LeftClavicle: SolverType.Look,
  RightClavicle: SolverType.Look,
  LeftArm: SolverType.Tri,
  RightArm: SolverType.Tri,
  LeftLeg: SolverType.Tri,
  RightLeg: SolverType.Tri,
  LeftToe: SolverType.Look,
  RightToe: SolverType.Look,
};

function isAppendageChainName(chainName: string): boolean {
  return APPENDAGE_CHAIN_PREFIXES.some(
    (prefix) => chainName === prefix || chainName.startsWith(`${prefix}_`),
  );
}

function getIterativeBaseChainName(chainName: string): string {
  return chainName.replace(ITERATIVE_CHAIN_PATTERN, '');
}

export function getDefaultSolver(chainName: string): SolverType {
  if (chainName in DEFAULT_SOLVERS) return DEFAULT_SOLVERS[chainName];
  const iterativeBaseChainName = getIterativeBaseChainName(chainName);
  if (iterativeBaseChainName in DEFAULT_SOLVERS) {
    return DEFAULT_SOLVERS[iterativeBaseChainName];
  }
  if (isAppendageChainName(chainName)) return SolverType.SpringChain;

  if (FINGER_ROLE_NAMES.some((pattern) => chainName.includes(pattern))) {
    return SolverType.Look;
  }
  if (chainName.includes('Toe')) return SolverType.Look;
  if (chainName.includes('Metacarpal')) return SolverType.Look;

  return SolverType.Look;
}

export function getEffectiveSolverType(
  chainName: string,
  solverType: string | null | undefined,
): string {
  return solverType ?? getDefaultSolver(chainName).valueOf();
}

export function isDefaultSolverType(
  chainName: string,
  solverType: string | null | undefined,
): boolean {
  return (
    getEffectiveSolverType(chainName, solverType) ===
    getDefaultSolver(chainName).valueOf()
  );
}

export function normalizeSolverType(
  chainName: string,
  solverType: string | null | undefined,
): string | null {
  if (solverType == null) return null;
  return isDefaultSolverType(chainName, solverType) ? null : solverType;
}

export function isSpringSolverType(
  solverType: string | null | undefined,
): boolean {
  return solverType === SolverType.SpringChain;
}

export function isSpringSolverProperties(
  solverProperties: ChainSolverProperties | null | undefined,
): solverProperties is SpringSolverProperties {
  if (!solverProperties) return false;
  return (
    typeof (solverProperties as SpringSolverProperties).stiffness === 'number' &&
    typeof (solverProperties as SpringSolverProperties).falloff === 'number' &&
    typeof (solverProperties as SpringSolverProperties).falloffCurve ===
      'string' &&
    typeof (solverProperties as SpringSolverProperties).gain === 'number'
  );
}

export function isIkSolverProperties(
  solverProperties: ChainSolverProperties | null | undefined,
): solverProperties is SolverProperties {
  if (!solverProperties) return false;
  return (
    typeof (solverProperties as SolverProperties).iterations === 'number' &&
    typeof (solverProperties as SolverProperties).reversePv === 'boolean' &&
    typeof (solverProperties as SolverProperties).maxStretch === 'number'
  );
}

function hasSpringSolverPropsShape(data: Record<string, unknown>): boolean {
  return (
    'stiffness' in data ||
    'falloff' in data ||
    'falloffCurve' in data ||
    'gain' in data
  );
}

function hasIkSolverPropsShape(data: Record<string, unknown>): boolean {
  return 'iterations' in data || 'revPole' in data || 'maxStretch' in data;
}

export function resolveSolverTypeForPropertiesParsing(
  chainName: string,
  solverType: string | null | undefined,
  solverPropsData: Record<string, unknown> | null | undefined,
): string {
  if (solverType) return solverType;
  if (solverPropsData) {
    const hasSpringProps = hasSpringSolverPropsShape(solverPropsData);
    const hasIkProps = hasIkSolverPropsShape(solverPropsData);
    if (hasSpringProps && !hasIkProps) return SolverType.SpringChain;
    if (hasIkProps && !hasSpringProps) return SolverType.Look;
  }
  return getEffectiveSolverType(chainName, null);
}

export function getDefaultSolverProperties(
  solverType: string,
): ChainSolverProperties {
  if (isSpringSolverType(solverType)) return new SpringSolverProperties();
  return new SolverProperties();
}

export function getEffectiveSolverProperties(
  solverType: string,
  solverProperties: ChainSolverProperties | null | undefined,
): ChainSolverProperties {
  if (isSpringSolverType(solverType)) {
    if (!isSpringSolverProperties(solverProperties)) {
      return getDefaultSolverProperties(solverType);
    }
    return new SpringSolverProperties(
      solverProperties.stiffness,
      solverProperties.falloff,
      solverProperties.falloffCurve,
      solverProperties.gain,
    );
  }

  if (!isIkSolverProperties(solverProperties)) {
    return getDefaultSolverProperties(solverType);
  }
  return new SolverProperties(
    solverProperties.iterations,
    solverProperties.reversePv,
    solverProperties.maxStretch,
  );
}

export function normalizeSolverProperties(
  solverType: string,
  solverProperties: ChainSolverProperties | null | undefined,
): ChainSolverProperties | null {
  const effective = getEffectiveSolverProperties(solverType, solverProperties);
  if (effective.isDefault) return null;
  return effective;
}

export function solverPropertiesFromDict(
  solverType: string,
  data: Record<string, unknown>,
): ChainSolverProperties {
  if (isSpringSolverType(solverType)) {
    return SpringSolverProperties.fromDict(data);
  }
  return SolverProperties.fromDict(data);
}

export function solverPropertiesToDict(
  solverType: string,
  solverProperties: ChainSolverProperties | null | undefined,
): Record<string, unknown> {
  return getEffectiveSolverProperties(solverType, solverProperties).toDict();
}

export function areSolverPropertiesEqual(
  left: ChainSolverProperties | null | undefined,
  right: ChainSolverProperties | null | undefined,
): boolean {
  if (!left && !right) return true;
  if (!left || !right) return false;

  const leftIsSpring = isSpringSolverProperties(left);
  const rightIsSpring = isSpringSolverProperties(right);
  if (leftIsSpring !== rightIsSpring) return false;

  if (leftIsSpring && rightIsSpring) {
    return (
      left.stiffness === right.stiffness &&
      left.falloff === right.falloff &&
      left.falloffCurve === right.falloffCurve &&
      left.gain === right.gain
    );
  }

  if (!isIkSolverProperties(left) || !isIkSolverProperties(right)) {
    return false;
  }
  return (
    left.iterations === right.iterations &&
    left.reversePv === right.reversePv &&
    left.maxStretch === right.maxStretch
  );
}
